import { useEffect, useState } from 'react';
import { applyTheme, getInitDataUnsafe, getLaunchDiag, hapticFeedback } from './lib/twa.js';
import { auth } from './api.js';
import EventList from './pages/EventList.jsx';
import EventDetail from './pages/EventDetail.jsx';
import EventForm from './pages/EventForm.jsx';
import StartBotNotice from './pages/StartBotNotice.jsx';
import NoChatNotice from './pages/NoChatNotice.jsx';

function useHashRoute() {
  const [hash, setHash] = useState(() => window.location.hash || '#/');
  useEffect(() => {
    const onChange = () => setHash(window.location.hash || '#/');
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

function parseRoute(hash) {
  const path = hash.replace(/^#/, '');
  if (!path || path === '/' || path === '') return { name: 'list' };
  if (path === '/new') return { name: 'new' };
  if (path.startsWith('/events/')) {
    const rest = path.slice('/events/'.length);
    if (rest.startsWith('edit/')) {
      return { name: 'edit', id: Number.parseInt(rest.slice('edit/'.length), 10) };
    }
    const id = Number.parseInt(rest, 10);
    if (Number.isFinite(id)) return { name: 'detail', id };
  }
  return { name: 'list' };
}

function readLaunchChatId() {
  try {
    const unsafe = getInitDataUnsafe();
    const fromInitChat = unsafe?.chat?.id;
    if (Number.isFinite(fromInitChat)) return fromInitChat;
    const params = new URLSearchParams(window.location.search);
    const raw =
      params.get('launch_chat_id') ||
      params.get('startapp') ||
      params.get('start_param') ||
      unsafe?.start_param;
    if (!raw) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export default function App() {
  const hash = useHashRoute();
  const route = parseRoute(hash);
  const [session, setSession] = useState({ state: 'loading' });
  const unsafe = getInitDataUnsafe();
  const launchChatId = readLaunchChatId();

  useEffect(() => {
    applyTheme();
    let cancelled = false;
    (async () => {
      try {
        const result = await auth({ launchChatId: launchChatId || undefined });
        if (cancelled) return;
        if (!result) {
          setSession({ state: 'no-initdata' });
          return;
        }
        if (result.user?.bot_blocked_at) {
          setSession({ state: 'bot-blocked' });
          return;
        }
        setSession({ state: 'authed', user: result.user, chat: result.chat });
      } catch (e) {
        if (cancelled) return;
        setSession({ state: 'error', error: e });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [launchChatId]);

  useEffect(() => {
    hapticFeedback('light');
  }, [route.name]);

  if (session.state === 'loading') {
    return <div className="notice">Loading…</div>;
  }

  if (session.state === 'error') {
    const code = session.error?.code;
    if (code === 'CHAT_NOT_ACCESSIBLE') return <NoChatNotice />;
    if (code === 'BOT_BLOCKED') return <StartBotNotice />;
    return (
      <div className="notice">
        <h2>Something went wrong</h2>
        <div>{session.error?.message || 'Unknown error'}</div>
      </div>
    );
  }

  if (session.state === 'no-initdata') {
    const diag = getLaunchDiag();
    return (
      <div className="notice">
        <h2>No Telegram identity</h2>
        <p>The Mini App opened without initData, so it cannot authenticate.</p>
        <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, textAlign: 'left' }}>
          {JSON.stringify(diag, null, 2)}
        </pre>
        <p>If <code>has_telegram</code> is false, the page opened in an external browser, not inside Telegram.</p>
      </div>
    );
  }

  if (session.state === 'bot-blocked') {
    return <StartBotNotice />;
  }

  const effectiveChat = session.chat || unsafe.chat || (launchChatId ? { id: launchChatId } : null);

  if (!effectiveChat) {
    return <NoChatNotice />;
  }

  return (
    <div className="app">
      {route.name === 'list' && <EventList chat={effectiveChat} />}
      {route.name === 'detail' && <EventDetail eventId={route.id} user={session.user} />}
      {route.name === 'new' && <EventForm chat={effectiveChat} />}
      {route.name === 'edit' && <EventForm chat={effectiveChat} eventId={route.id} />}
    </div>
  );
}
